import React, { useEffect, useState } from 'react';
import { updateApiHeaders } from '../api/ApiThunks';
import LocalStorage from '../../services/LocalStorage';
import { RootState, store, useAppSelector } from '../store';
import { createSelector } from '@reduxjs/toolkit';
import { Device } from '../../services/Native/Device';
import { TenantId } from '../selectors';
import version from 'config/version.json';

const DynamicHeaders = createSelector(
  (state: RootState) => state.authentication.role,
  (state: RootState) => state.authentication.token,
  (state: RootState) => state.device,
  (state: RootState) => TenantId(state),
  (role, token, device, tenantId) => {
    const nativePlatform =
      device.clientPlatform ??
      (Device.isIOS() ? 'ios' : Device.isAndroid() ? 'android' : undefined);

    let clientName: string;
    if (nativePlatform) {
      const simpleRole = !role || role === 'patient' ? 'patient' : 'team';
      const clientRole = device.clientRole ?? 'patient';
      clientName = `${nativePlatform}-${clientRole ?? simpleRole}`;
    } else {
      const portalPrefix = role === 'patient' ? 'patient-' : role === 'manager' ? 'tenant-' : '';
      clientName = `${portalPrefix}portal`;
    }

    return {
      'client-name': clientName,
      tenant_id: tenantId,
      'client-version': device.clientVersion ?? version['client-version'],
    };
  },
);

/*
 * Causes a rerender after warm-up to ensure warmup data is loaded
 */
export function useWaitForWarmup() {
  const [warmedUp, setWarmedUp] = useState(false);
  const dynamicHeaders = useAppSelector(DynamicHeaders);

  useEffect(() => {
    if ('careand_warmup' in window && window.careand_warmup instanceof Promise) {
      window.careand_warmup.finally(() => {
        setWarmedUp(true);
        const headers = DynamicHeaders(store.getState());
        updateApiHeaders(headers);
      });
    }
  }, []);

  useEffect(() => {
    updateApiHeaders(dynamicHeaders);
  }, [dynamicHeaders]);

  return warmedUp;
}
